<template>
  <main class="pb-20">
    <HeaderMenu>
      <div class="flex gap-3">
        <div
          class="relative inline-block"
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
        >
          <BaseButton
            class="border-red-500 hover:bg-red-50 font-lato text-sm text-red-500"
            data-cy="unit-detail__button--delete"
            :disabled="unit.is_deleteable === false"
            @click="toggleDeletePrompt"
          >
            <template #icon-left>
              <JdsIcon
                name="trash"
                size="16px"
                class="text-red-500"
              />
            </template>
            Hapus
          </BaseButton>

          <div
            v-if="showTooltip && unit.is_deleteable === false"
            class="absolute top-full right-0 mt-2 px-2 py-1 bg-white text-black rounded border border-gray-300 shadow-lg whitespace-nowrap"
            style="font-size: 10px; z-index: 9999;"
          >
            Tidak dapat melakukan hapus karena sudah terelasi
            <div
              class="absolute bottom-full right-3 w-0 h-0 border-l-[5px] border-r-[5px] border-b-[5px] border-l-transparent border-r-transparent border-b-white"
            />
          </div>
        </div>

        <LinkButton
          :href="`/profil-jawa-barat/tentang/perangkat-daerah/${$route.params.id}/ubah`"
          variant="secondary"
          data-cy="unit-detail__button--update"
          class="hover:bg-green-50"
        >
          <template #icon-left>
            <JdsIcon
              name="pencil"
              size="16px"
              class="h-4 text-green-700"
            />
          </template>
          <p class="font-lato font-bold text-sm text-green-700">
            Ubah Data
          </p>
        </LinkButton>
      </div>
    </HeaderMenu>

    <section class="px-6 py-4 rounded-lg bg-white mb-4">
      <h1
        data-cy="unit-detail__title"
        class="font-roboto font-medium text-[21px] leading-[34px] text-blue-gray-700 mb-3"
      >
        Detail Perangkat Daerah
      </h1>

      <div class="flex flex-col gap-4">
        <div class="flex justify-center items-center w-full h-full bg-gray-100 rounded-lg py-8">
          <div
            v-if="loading"
            class="h-28 w-28 rounded-lg animate-pulse bg-gray-200"
          />
          <img
            v-else
            class="h-full w-full object-contain"
            :src="unit.logo"
            :style="{ height: '144px', width: 'auto' }"
            alt="logo perangkat daerah"
            data-cy="unit-detail__logo"
          >
        </div>

        <div
          data-cy="unit-detail__section-unit"
          class="rounded-lg overflow-hidden border border-gray-200"
        >
          <JdsSimpleTable class="!table-auto">
            <thead>
              <tr>
                <th
                  id="about-unit"
                  colspan="2"
                  class="!font-roboto !text-sm"
                >
                  Info Detail
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  headers="about-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Nama Perangkat Daerah
                </td>
                <td
                  headers="about-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ unit.name || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Nama Pimpinan
                </td>
                <td
                  headers="about-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ unit.chief || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  No Telepon
                </td>
                <td
                  headers="about-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ unit.phone || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="location-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Link Website
                </td>
                <td
                  headers="location-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/5 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    <template v-if="unit.website">
                      <a
                        :href="unit.website"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline text-blue-gray-500 break-all text-base font-lato font-bold"
                      >
                        {{ unit.website }}
                      </a>
                    </template>

                    <template v-else>
                      <span>-</span>
                    </template>
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="location-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Link PPID
                </td>
                <td
                  headers="location-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/5 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    <template v-if="unit.ppid">
                      <a
                        :href="unit.ppid"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline text-blue-gray-500 break-all text-base font-lato font-bold"
                      >
                        {{ unit.ppid }}
                      </a>
                    </template>

                    <template v-else>
                      <span>-</span>
                    </template>
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Alamat
                </td>
                <td
                  headers="about-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ unit.address || '-' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  headers="about-unit"
                  class="min-w-[228px] font-lato text-blue-gray-500 font-bold text-sm"
                >
                  Update Terakhir
                </td>
                <td
                  headers="about-unit"
                  class="w-full font-lato text-blue-gray-500 text-sm"
                >
                  <div
                    v-if="loading"
                    class="h-4 w-1/4 rounded-lg animate-pulse bg-gray-200"
                  />
                  <div v-else>
                    {{ lastUpdate }}
                  </div>
                </td>
              </tr>
            </tbody>
          </JdsSimpleTable>
        </div>
      </div>
    </section>

    <BaseModal
      :open="isDeletePromptOpen"
      data-cy="unit-detail__modal"
      @close="toggleDeletePrompt"
    >
      <div class="w-full h-full">
        <h1 class="font-roboto text-xl leading-8 font-medium text-green-700 mb-6">
          Menghapus Data
        </h1>
        <p class="font-lato text-sm text-gray-800">
          Apakah Anda yakin ingin menghapus Data ini?
        </p>
      </div>
      <template #footer>
        <div class="flex gap-4 justify-end">
          <BaseButton
            class="border-green-700 hover:bg-green-50 text-sm text-green-700"
            data-cy="unit-detail__modal-button--cancel"
            @click="toggleDeletePrompt"
          >
            Batal
          </BaseButton>
          <BaseButton
            class="bg-red-500 hover:bg-red-400 text-sm text-white"
            :disabled="deleteLoading"
            data-cy="unit-detail__modal-button--delete"
            @click="deleteUnit(unit.id)"
          >
            <p v-if="!deleteLoading">
              Ya, saya yakin
            </p>
            <p
              v-else
              class="flex gap-2 items-center text-gray-500"
            >
              <JdsSpinner
                size="16"
                foreground="#757575"
              />
              Loading...
            </p>
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </main>
</template>

<script>
import { formatDate } from '@/common/helpers/date';
import { EventBus } from '@/common/helpers/event-bus';
import BaseButton from '@/common/components/BaseButton';
import BaseModal from '@/common/components/BaseModal';
import LinkButton from '@/common/components/LinkButton';
import HeaderMenu from '@/common/components/HeaderMenu';

import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const unitRepository = RepositoryFactory.get('unit');

export default {
  name: 'UnitDetail',
  components: {
    HeaderMenu,
    BaseButton,
    LinkButton,
    BaseModal,
  },
  data() {
    return {
      unit: {},
      loading: false,
      deleteLoading: false,
      isPreviewModalOpen: false,
      isDeletePromptOpen: false,
      showTooltip: false,
    };
  },
  computed: {
    lastUpdate() {
      if (!this.unit.updated_at) return '-';
      return `Terakhir disimpan pada: ${formatDate(this.unit.updated_at, 'dd/MM/yyyy - HH:mm')}`;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      const { id } = this.$route.params;
      const response = await unitRepository.getUnitById(id);
      const { data } = response;

      if (data.data) {
        this.unit = data.data;
      }
    } catch (error) {
      if (error?.response.status === 403) {
        EventBus.$emit('error:forbidden');
      } else {
        this.$toast({
          type: 'error',
          message: 'Gagal mendapatkan data Perangkat Daerah, silakan coba beberapa saat lagi',
        });
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    /**
     * Delete unit by id
     * @param {number} id - id of unit to delete
     */
    async deleteUnit(id) {
      try {
        this.deleteLoading = true;
        await unitRepository.deleteUnitById(id);
        this.$toast({
          type: 'success',
          message: 'Data Perangkat Daerah telah berhasil dihapus',
        });
      } catch (error) {
        this.$toast({
          type: 'error',
          message: 'Data Perangkat Daerah gagal dihapus',
        });
      } finally {
        this.deleteLoading = false;
        this.toggleDeletePrompt();
        this.$router.back();
      }
    },
    toggleDeletePrompt() {
      this.isDeletePromptOpen = !this.isDeletePromptOpen;
    },
  },
};
</script>
